import React, { JSX, useEffect, useState, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { Dialog, DialogActions, DialogContent, Theme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useForm } from 'react-hook-form';

import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { getTranslateByKey } from '@/main/services/utils';
import { getDefaultCountryOptionDrop } from '@/main/services/utils';
import { reasonApi } from '@/main/api';
import { useCountryReducer } from '@/main/store/country';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { KeyValue, KeyValueDefault } from '@/main/models/common';
import { ReasonFilterRequest } from '@/main/models/request';

import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KOGFileUploader from '@/main/components/shared/fileUploader/fileUploader';
import KogModalSlideImages from '@/main/components/shared/KogModalSlideImages/modalSlideImages';

import * as S from './styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiPaper-root': {
        padding: '1.5rem !important ',
        maxWidth: '500px'
      }
    },
    actions: {
      padding: '1.5rem 0 0'
    },
    paper: {
      padding: '0',
      overflowX: 'hidden'
    }
  })
);

export interface IConfirmReasonResult {
  confirmed: boolean;
  reason?: KeyValueDefault | null;
  observation?: string;
  files?: File[];
}

export interface IAsyncModalConfirmReasonProps {
  closeByOverlay?: boolean;
}

export interface IAsyncModalConfirmReasonRef {
  openModal: (text: string, idReasonType?: number) => Promise<IConfirmReasonResult>;
}

const createPromise = (): [
  Promise<IConfirmReasonResult>,
  (value: IConfirmReasonResult | PromiseLike<IConfirmReasonResult>) => void
] => {
  let resolver: (value: IConfirmReasonResult | PromiseLike<IConfirmReasonResult>) => void;
  const promise = new Promise<IConfirmReasonResult>(resolve => {
    resolver = resolve;
  });
  return [promise, resolver!];
};

const ConfirmationReasonDialog = forwardRef<IAsyncModalConfirmReasonRef, IAsyncModalConfirmReasonProps>(({
  closeByOverlay = false
}, ref) => {
  const classes = useStyles();
  const countryReducer = useCountryReducer();
  const [reasons, setReasons] = useState<KeyValueDefault[]>([]);
  const [isLoadingReasons, setIsLoadingReasons] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);
  const [label, setLabel] = useState<string>('');
  const [currentReasonType, setCurrentReasonType] = useState<number | null>(null);
  const [resolver, setResolver] = useState<
    ((value: IConfirmReasonResult | PromiseLike<IConfirmReasonResult>) => void) | null
  >(null);

  const [selectedReason, setSelectedReason] = useState<KeyValueDefault | null>(null);
  const [observationReason, setObservationReason] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);

  const [imageIndex, setImageIndex] = useState(0);
  const [isOpenModalFade, setIsOpenModalFade] = useState<boolean>(false);
  const [images, setImages] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    openModal: (text: string, idReasonType?: number) => {
      setLabel(text);
      setCurrentReasonType(idReasonType || null);
      setOpen(true);

      const [promise, resolve] = createPromise();
      setResolver(() => resolve);
      return promise;
    }
  }));

  const handleCloseModal = useCallback((result: IConfirmReasonResult) => {
    setOpen(false);
    if (resolver) {
      resolver(result);
    }
  }, [resolver]);

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.countriesUser.data
  );

  const INITIAL_FORM_STATE = {
    reason: null as KeyValueDefault | null,
    observation: ''
  };

  type IReasonForm = {
    reason: KeyValueDefault | null;
    observation: string;
  };

  const methodSave = useForm<IReasonForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });

  const isDisabledObservation = () => convertStringForBoolean(selectedReason?.default);

  useEffect(() => {
    if (open && optionDefaultCountryDrop !== null && currentReasonType) {
      setIsLoadingReasons(true);
      const requestReason: ReasonFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        idReasonType: currentReasonType
      };
      reasonApi.getDomain(requestReason)
        .then(response => {
          if (response.data.result) {
            setReasons(response.data.result);
          }
        })
        .catch(() => {})
        .finally(() => {
          setIsLoadingReasons(false);
        });
    }
  }, [optionDefaultCountryDrop, currentReasonType, open]);

  useEffect(() => {
    if (open) {
      setSelectedReason(null);
      setObservationReason('');
      setFiles([]);
      methodSave.reset(INITIAL_FORM_STATE);
    }
  }, [open]);

  useEffect(() => {
    if (files && files.length > 0) {
      let imagesString: string[] = [];
      files.map((item) => {
        if (item as File && item.type.startsWith('image')) {
          imagesString.push(URL.createObjectURL(item as File));
        }
      });
      setImages(imagesString);
    } else {
      setImages([]);
    }
  }, [files]);

  function handleClickPreview(index: number, urlFile: string) {
    const clickedFile = files[index];
    if (clickedFile && clickedFile.type.startsWith('image')) {
      let imageCount = 0;
      let targetIndex = 0;
      for (let i = 0; i <= index; i++) {
        if (files[i].type.startsWith('image')) {
          targetIndex = imageCount;
          imageCount++;
        }
      }
      setImageIndex(targetIndex);
      setIsOpenModalFade(true);
    }
  }

  function handleClickNo(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    handleCloseModal({ confirmed: false });
  }

  function onSubmitSave() {
    handleCloseModal({
      confirmed: true,
      reason: selectedReason,
      observation: observationReason,
      files: files
    });
  }

  const handleClose = () => {
    if (closeByOverlay) {
      handleCloseModal({ confirmed: false });
    }
  };

  const hasChanges = methodSave.formState.isDirty;

  return (
    <Dialog open={open} className={classes.root} onClose={handleClose}>
      <DialogContent className={classes.paper}>
        <S.Container>
          <S.Text>{label}</S.Text>
          <form id="form-modal-confirm-reason" onSubmit={methodSave.handleSubmit(onSubmitSave)} noValidate>
            <S.InputGroup>
              <S.FieldWrapper>
                <KogAutoCompleteControlled
                  id="id-reasonConfirm-autocomplete"
                  className="ignore-click"
                  required
                  label={getTranslateByKey('information.modificationReason')}
                  nameCustom="reason"
                  errorMessageCustom=""
                  control={methodSave.control}
                  setValueCustom={methodSave.setValue}
                  options={reasons}
                  isLoading={isLoadingReasons}
                  rulesCustom={{ required: true }}
                  hasErrorCustom={false}
                  onChangeCustom={(optionSelected: KeyValue | KeyValueDefault | null) => {
                    // @ts-ignore
                    setSelectedReason(optionSelected);
                    // @ts-ignore
                    methodSave.setValue('reason', optionSelected, { shouldValidate: true });
                  }}
                />
              </S.FieldWrapper>
              {isDisabledObservation() && (
                <S.FieldWrapper>
                  <KogInputTextControlledNew
                    id="id-reasonConfirm-text-observation"
                    control={methodSave.control}
                    name="observation"
                    onChangeCustom={e => setObservationReason(e)}
                    label={getTranslateByKey('information.observation')}
                    required
                    errorMessageCustom={
                      methodSave.formState.errors.observation?.type === 'required'
                        ? getTranslateByKey('information.observationRequired')
                        : ''
                    }
                    controllerProps={{ rules: { required: true } }}
                  />
                </S.FieldWrapper>
              )}
              <S.FieldWrapperFile>
                <KOGFileUploader
                  isFullScreenDragDrop={true}
                  files={files}
                  setFiles={setFiles}
                  typeFiles={['image', 'pdf', 'document', 'excel', 'email']}
                  maxFiles={10}
                  handleClickPreviewCustom={handleClickPreview}
                />
              </S.FieldWrapperFile>
            </S.InputGroup>
          </form>
          {images && images.length > 0 && (
            <KogModalSlideImages
              images={images}
              imageIndex={imageIndex}
              isOpen={isOpenModalFade}
              setIsOpen={setIsOpenModalFade}
            />
          )}
        </S.Container>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <S.AreaFooterButtons>
          <SecondaryButton id="id-btn-cancel-reason" onClick={handleClickNo}>
            {getTranslateByKey('information.cancel')}
          </SecondaryButton>
          <Button id="id-btn-save-reason" type="submit" form="form-modal-confirm-reason" disabled={!hasChanges}>
            {getTranslateByKey('information.save')}
          </Button>
        </S.AreaFooterButtons>
      </DialogActions>
    </Dialog>
  );
});

const useAsyncModalConfirmReason = (): [
  (text: string, idReasonType?: number) => Promise<IConfirmReasonResult>,
  (props: IAsyncModalConfirmReasonProps) => JSX.Element
] => {
  const modalRef = useRef<IAsyncModalConfirmReasonRef>(null);

  const getConfirmationReason = useCallback((text: string, idReasonType?: number): Promise<IConfirmReasonResult> => {
    if (modalRef.current) {
      return modalRef.current.openModal(text, idReasonType);
    }
    return Promise.resolve({ confirmed: false });
  }, []);

  const ModalConfirmationReason = useCallback(({
    closeByOverlay = false
  }: IAsyncModalConfirmReasonProps): JSX.Element => {
    return (
      <ConfirmationReasonDialog
        ref={modalRef}
        closeByOverlay={closeByOverlay}
      />
    );
  }, []);

  return [getConfirmationReason, ModalConfirmationReason];
};

export default useAsyncModalConfirmReason;
